import { AlertTriangle } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { Select } from '@/components/ui/Select';
import { TextField } from '@/components/ui/TextField';

import { SAP_MODULES, type ProposalView } from '../code-proposal';

// Código del alta (G3-2, 04-GC-P08 §B.1): el servidor propone el siguiente consecutivo
// y se muestra en solo lectura con "Editar código"; un tipo libre pide escribirlo; un
// registro SAP pide antes el módulo. Si el proceso no tiene tipo, lleva a Catálogos.

const SAP_OPTIONS = SAP_MODULES.map((m) => ({ value: m.code, label: `${m.code} · ${m.label}` }));

interface Props {
  view: ProposalView;
  editing: boolean;
  code: string;
  error: string | null;
  showSap: boolean;
  sapModule: string;
  onSapModule: (code: string) => void;
  onCode: (code: string) => void;
  onEdit: () => void;
  onUseProposed: () => void;
  onRetry: () => void;
}

function Note({ children }: { children: string }): ReactElement {
  return (
    <div className="fld">
      <span className="fld-lab">Código</span>
      <p className="fld-note">{children}</p>
    </div>
  );
}

function CodeState(p: Props): ReactElement {
  const { view } = p;
  switch (view.kind) {
    case 'idle':
      return <Note>Elige el tipo y el proceso para proponer el código.</Note>;
    case 'loading':
      return <Note>Calculando el código…</Note>;
    case 'needs_sap':
      return <Note>Elige el módulo de SAP para proponer el código.</Note>;
    case 'blocked':
      return (
        <>
          <div className="callout warn" role="alert">
            <AlertTriangle size={16} aria-hidden="true" />
            <div>
              {view.message}
              {view.toCatalogs && (
                <>
                  {' '}
                  <Link to="/configuracion/catalogos?catalogo=procesos">Ir a Catálogos</Link>
                </>
              )}
            </div>
          </div>
          {view.allowManual && (
            <TextField
              label="Código"
              value={p.code}
              maxLength={60}
              mono
              hint="Sin tipo de proceso no hay patrón: el código escrito se acepta tal cual."
              error={p.error}
              onChange={p.onCode}
            />
          )}
        </>
      );
    case 'error':
      return (
        <div className="callout warn" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <div>
            {view.message}{' '}
            <button type="button" className="btn mut sm" onClick={p.onRetry}>
              Reintentar
            </button>
          </div>
        </div>
      );
    case 'free':
      return (
        <TextField
          label="Código"
          value={p.code}
          maxLength={60}
          placeholder="MAN-CAL-01"
          mono
          hint="Este tipo no tiene patrón: escribe el código."
          error={p.error}
          onChange={p.onCode}
        />
      );
    case 'proposed':
      return p.editing ? (
        <div className="fld">
          <TextField
            label="Código"
            value={p.code}
            maxLength={60}
            mono
            hint={`Debe seguir el patrón del proceso y tipo, como ${view.code}.`}
            error={p.error}
            onChange={p.onCode}
          />
          <div>
            <button type="button" className="btn mut sm" onClick={p.onUseProposed}>
              Usar el propuesto
            </button>
          </div>
        </div>
      ) : (
        <div className="fld">
          <span className="fld-lab">Código propuesto</span>
          <div className="value-chosen">
            <strong className="code">{view.code}</strong>
            <button type="button" className="btn mut sm" onClick={p.onEdit}>
              Editar código
            </button>
          </div>
          <span className="fld-hint">
            Se confirma al reservar: si otra persona toma este número antes, se asigna el siguiente.
          </span>
          {p.error !== null && <span className="fld-err">{p.error}</span>}
        </div>
      );
  }
}

export function CodeField(p: Props): ReactElement {
  return (
    <>
      {p.showSap && (
        <label className="fld">
          <span className="fld-lab">Módulo de SAP</span>
          <Select
            value={p.sapModule}
            placeholder="Elige el módulo…"
            options={SAP_OPTIONS}
            onChange={p.onSapModule}
          />
        </label>
      )}
      <CodeState {...p} />
    </>
  );
}
